import type { TenantSchool } from '../../state/useSchoolContext';
import { EngineeringSupportBanner } from '../overlays/EngineeringSupportBanner';

type SchoolContextRecoveryCardProps = {
  subject: string;
  mode: 'loading' | 'selection-required' | 'no-membership' | 'failure';
  schools?: TenantSchool[];
  errorMessage?: string | null;
  isSwitching?: boolean;
  onSelectSchool?: (schoolId: string) => void;
};

export function SchoolContextRecoveryCard({
  subject,
  mode,
  schools = [],
  errorMessage = null,
  isSwitching = false,
  onSelectSchool,
}: SchoolContextRecoveryCardProps) {
  if (mode === 'loading') {
    return (
      <section className="mx-auto max-w-[960px] p-6 md:p-8">
        <div className="rounded-[1.5rem] border border-dashed border-gray-200 bg-white px-6 py-16 text-center text-text-secondary shadow-sm">
          Resolving active school context for {subject.toLowerCase()}…
        </div>
      </section>
    );
  }

  if (mode === 'failure') {
    return (
      <section className="mx-auto max-w-[960px] p-6 md:p-8">
        <div className="rounded-[1.5rem] border border-dashed border-coral/30 bg-white px-6 py-16 text-center shadow-sm">
          <EngineeringSupportBanner className="mx-auto mb-5 max-w-xl text-left" />
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-coral">Execution Context Failure</p>
          <p className="mt-3 text-base text-irongrey">
            {errorMessage || `Unable to resolve active school context for ${subject.toLowerCase()}.`}
          </p>
        </div>
      </section>
    );
  }

  if (mode === 'no-membership') {
    return (
      <section className="mx-auto max-w-[960px] p-6 md:p-8">
        <div className="rounded-[1.5rem] border border-dashed border-coral/30 bg-white px-6 py-16 text-center shadow-sm">
          <EngineeringSupportBanner className="mx-auto mb-5 max-w-xl text-left" />
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-coral">Execution Context Blocker</p>
          <p className="mt-3 text-base text-irongrey">
            {subject} could not find any school memberships for this session. Get the actor into a school scope and retry.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-[960px] p-6 md:p-8">
      <div className="rounded-[1.5rem] border border-dashed border-coral/30 bg-white px-6 py-16 text-center shadow-sm">
        <EngineeringSupportBanner className="mx-auto mb-5 max-w-xl text-left" />
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-coral">Execution Context Blocker</p>
        <p className="mt-3 text-base text-irongrey">
          {subject} requires an explicit school selection before live data can load.
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          {schools.map((school) => (
            <button
              key={school.schoolId}
              type="button"
              data-testid={`school-context-option-${school.schoolId}`}
              onClick={() => onSelectSchool?.(school.schoolId)}
              disabled={isSwitching}
              className="rounded-full border border-border bg-white px-4 py-2 text-sm font-medium text-irongrey transition-colors hover:border-primary hover:text-primary disabled:cursor-wait disabled:opacity-60"
            >
              {school.schoolName}
            </button>
          ))}
        </div>
        {isSwitching ? (
          <p className="mt-4 text-sm text-text-secondary">Switching school context…</p>
        ) : null}
      </div>
    </section>
  );
}
